import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import { decamelizeKeys, camelizeKeys } from 'humps';
import { ApiError } from 'service/error';
import history from 'service/history';
import { rootStore } from 'store/root';
import { expireSession } from './session-api';
// import plex from './plex';

// https://github.com/softonic/axios-retry/issues/87
const retryDelay = (retryNumber = 0) => {
    const seconds = Math.pow(2, retryNumber) * 2000;
    const randomMs = 1000 * Math.random();
    return seconds + randomMs;
};

const { settings, application } = window;

axios.defaults.headers.common['x-arena-fe-version'] = `nxl-${process.env.REACT_APP_VERSION}`;
axios.defaults.headers.common['x-nxl-session-id'] = application.sessionId;

export const CmsApi = axios.create({
    baseURL: settings.hosts.cdn,
});

CmsApi.interceptors.request.use((config) => {
    if (config.url)
        config.url = config.url
            .replace('{regionCode}', rootStore.regionConfig.cmsCode)
            .replace('{regionWebCode}', rootStore.regionConfig.cmsWebCode);
    if (rootStore.session.isPreview) {
        config.baseURL = rootStore.regionConfig.cdnHost;
    }
    return config;
});

CmsApi.interceptors.response.use((response) => response.data);

const apiErrorHandling = (error: AxiosError) => {
    const url = error.config.url as string;
    let code = 0;
    let status = 0;
    let data = null;

    if (error.response) {
        code = parseInt(error.response.headers['x-arena-web-errorcode'], 10);
        data = error.response.data;
        status = error.response.status;
    } else {
        // 'AXIOS_ERROR';
        if (error.isAxiosError) code = 5;
        // 'NETWORK_ERROR';
        if (error.message === 'Network Error') code = 10;
        // 'TIMEOUT';
        if (error.code === 'ECONNABORTED') code = 20;
    }

    // session expired
    if (status === 401 && code === 10001) {
        if (history.location.pathname !== '/login') {
            expireSession(`api - 10001 :${url}`);
        } else {
            return Promise.reject({ url, status, code, data, message: error.message });
        }
    }

    if (status === 403 && code == null) data = ''; // remove general 403 error html body
    const e = new ApiError(status, code, url, data, error.message);
    return Promise.reject(e);
};

const requestInterceptor = (request: AxiosRequestConfig) => {
    // convert all query params keys to snake_case
    if (request.method === 'get' && request.params) {
        request.params = decamelizeKeys(request.params);
    }

    return request;
};

export const AccountApi = axios.create({
    baseURL: `${settings.hosts.accounts}/api/account/v1`,
});

axiosRetry(AccountApi, {
    retries: 3,
    retryDelay,
});

AccountApi.interceptors.response.use((response) => response.data, apiErrorHandling);
AccountApi.interceptors.request.use(requestInterceptor);

export const CoreApi = axios.create({
    baseURL: `${settings.hosts.accounts}/api`,
});

axiosRetry(CoreApi, {
    retries: 3,
    retryDelay,
});

CoreApi.interceptors.response.use((response) => response, apiErrorHandling);
CoreApi.interceptors.request.use(requestInterceptor);

export const CommerceApi = axios.create({
    baseURL: `${settings.hosts.accounts}/api/wallet/v1`,
});

CommerceApi.interceptors.response.use((response) => response.data);

export const RegionalApi = axios.create({
    baseURL: `${settings.hosts.accounts}/api/regional-auth/v1.0`,
});

axiosRetry(RegionalApi, {
    retries: 3,
    retryDelay,
});

RegionalApi.interceptors.response.use((response) => response.data, apiErrorHandling);

const iasHosts = {
    test: 'https://sandbox.api.nexon.com/ias/qa/public',
    pre: 'https://public.api.nexon.com/ias/pre/public',
    live: 'https://public.api.nexon.com/ias/live/public',
};

export const IASapi = axios.create({
    baseURL: iasHosts[window.application.params.environment as keyof typeof iasHosts] ?? iasHosts['live'],
});

const sdkHosts = {
    dev: 'https://dev-public.api.nexon.com/sdk-api',
    test: 'https://test-public.api.nexon.com/sdk-api',
    pre: 'https://public.api.nexon.com/sdk-api/pre',
    live: 'https://public.api.nexon.com/sdk-api',
};

const sdkApiResponseInterceptor = (response: AxiosResponse): AxiosResponse => {
    response.data = camelizeKeys(response.data);
    return response;
};

export const SDKapi = axios.create({
    // TODO: sdk api는 무조건 live환경을 보고, 전달하는 gid로 어떤 환경을 바라볼지 결정
    baseURL: sdkHosts['live'],
});

SDKapi.interceptors.response.use(sdkApiResponseInterceptor);
SDKapi.interceptors.request.use((config) => {
    if (config.url) {
        config.url = config.url.replace('{nexonlinkRegion}', rootStore.regionConfig.nexonlinkRegion);
    }
    return config;
});

const communityHosts = {
    test: 'https://public.api.nexon.com/community-ext-api',
    pre: 'https://public.api.nexon.com/community-ext-api',
    live: 'https://public.api.nexon.com/community-ext-api',
};

export const CommunityApi = axios.create({
    baseURL: communityHosts[window.application.params.environment as keyof typeof communityHosts] ?? communityHosts['live'],
    headers: {
        'x-inface-api-key': process.env.REACT_APP_COMMU_API_KEY,
        'Content-Type': 'application/json',
    },
});

// 메이플 월드는 test환경 사용하므로 별도로 관리
const mapleWordlCommunityHosts = {
    test: 'https://public.api.nexon.com/community-ext-api/pre',
    pre: 'https://public.api.nexon.com/community-ext-api',
    live: 'https://public.api.nexon.com/community-ext-api',
};

export const mapleWorldCommunityApi = axios.create({
    baseURL:
        mapleWordlCommunityHosts[window.application.params.environment as keyof typeof mapleWordlCommunityHosts] ??
        mapleWordlCommunityHosts['live'],
    headers: {
        'x-inface-api-key': process.env.REACT_APP_COMMU_API_KEY,
        'Content-Type': 'application/json',
    },
});
